import * as React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { rattacherAthlete, type AthleteRattache } from '../Auth/moi.ts';

/**
 * Invitation d'un athlete par l'adresse de son compte Google (page Athletes) :
 * elle lui est proposee a sa prochaine connexion. Rend la liste mise a jour
 * et l'adresse invitee.
 */
export default function InviterAthlete({ ouvert, fermer, invite }: {
  ouvert: boolean;
  fermer: () => void;
  invite: (athletes: AthleteRattache[], email: string) => void;
}) {
  const [email, setEmail] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [erreur, setErreur] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (ouvert) {
      setEmail('');
      setErreur(null);
    }
  }, [ouvert]);

  const inviter = async () => {
    const adresse = email.trim();
    if (!adresse) return;
    setEnCours(true);
    setErreur(null);
    try {
      invite(await rattacherAthlete(adresse), adresse);
      fermer();
    } catch (e) {
      setErreur('Invitation impossible : ' + (e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={ouvert} onClose={fermer} fullWidth maxWidth="xs">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          inviter();
        }}
      >
        <DialogTitle>Inviter un athlète</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            L'invitation lui est proposée à sa prochaine connexion. Une fois acceptée, tu établis son calendrier avec
            tes séances ; lui le consulte sans pouvoir le modifier.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="email"
            label="Adresse Google de l'athlète"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {erreur && <Alert severity="error" sx={{ mt: 2 }}>{erreur}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={fermer}>Annuler</Button>
          <Button type="submit" variant="contained" disabled={enCours || !email.trim()}>Inviter</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
